import {
  TimedFeatureImagePostion,
  TimedFeatureListPropsModel,
  TimedFeatureListVariant,
} from "@models/strapi-components/timed-feature-list";
import React, { useEffect, useState } from "react";
import TimedFeature from "./TimedFeature";
import { GatsbyImage, ImageDataLike, getImage } from "gatsby-plugin-image";
import { useTimer } from "react-timer-hook";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import CarouselComponent from "@components/common/Carousel/ReactCarousel";
import ImageRenderer from "@components/common/Image";
import Title from "@components/common/Title";
import Link from "@components/common/Link";
import useIcons from "@hooks/use-source-icons";
import { twMerge } from "tailwind-merge";
import VizWrapper from "@components/common/Base/VizWrapper";

const TimedFeatureList: React.FC<TimedFeatureListPropsModel> = ({
  componentData,
}) => {
  const {
    title,
    subTitle,
    learnMoreText,
    learnMoreLink,
    timedFeatures,
    color,
    times = 5,
    progressEmptyColor,
    progressFillColor,
    bgColor,
    variant = TimedFeatureListVariant.CIRCULAR,
    imgPosition,
  } = componentData;
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0);
  const { getFromReactIcons } = useIcons();
  const { restart } = useTimer({
    onExpire: goToNextFeature,
    expiryTimestamp: dayjs().add(5, "second").toDate(),
    autoStart: true,
  });
  //  ########################## Derived State ###########################################################

  const selectedFeature = timedFeatures?.[selectedFeatureIndex];
  const selectedFeatureId = selectedFeature?.id;

  const { featureImage } = selectedFeature ?? {};
  const selectedImageData = getImage(
    featureImage?.localFile as ImageDataLike | null
  );

  const isImageLeft = imgPosition === TimedFeatureImagePostion.LEFT;
  const imagePositionClass = isImageLeft ? "flex-row-reverse" : "flex-row";

  //  ########################## Side Effects ########################################################

  useEffect(() => {
    restart(dayjs().add(5, "second").toDate(), true);
  }, [selectedFeatureIndex]);

  //  ########################## Handler Functions ########################################################
  function goToNextFeature() {
    const timedFeaturesLength = timedFeatures?.length ?? 0;
    if (timedFeaturesLength > 0) {
      const nextFeatureIndex = (selectedFeatureIndex + 1) % timedFeaturesLength;
      setSelectedFeatureIndex(nextFeatureIndex);
    }
  }

  //  ########################## Render Functions ########################################################

  const renderTimedFeatureList = () =>
    timedFeatures?.map((timedFeature, index) => (
      <TimedFeature
        times={times}
        key={timedFeature.id}
        index={index}
        timedFeature={timedFeature}
        color={color}
        selectedFeatureId={selectedFeatureId ?? ""}
        setSelectedFeatureIndex={setSelectedFeatureIndex}
        progressEmptyColor={progressEmptyColor}
        progressFillColor={progressFillColor}
        variant={variant}
      />
    ));

  const renderTimedFeatureForMobileView = () => {
    return timedFeatures?.map((timedFeature, index) => {
      const { featureTitle, featureSubTitle, featureImage, featureIcon } =
        timedFeature;
      const Icon = getFromReactIcons(featureIcon);
      return (
        <div
          className={twMerge(
            "h-full sm:flex items-start sm:gap-4",
            imagePositionClass
          )}
          key={index}
        >
          <div className="basis-1/3 flex flex-col justify-center sm:h-full mb-8 sm:mb-0">
            <div className="flex items-center mb-2">
              <div
                className="shrink-0 flex items-center justify-center mr-3"
                style={{ borderColor: color }}
              >
                {Icon && <Icon size={20} />}
              </div>
              <Title
                variant="h3"
                titleText={featureTitle}
                className="text-left mb-0"
              />
            </div>
            <div>
              <Title
                variant="paragraph"
                titleText={featureSubTitle}
                className="text-left"
              />
            </div>
          </div>
          <div className="flex items-center sm:items-start sm:basis-2/3">
            <ImageRenderer image={featureImage} className="" />
          </div>
        </div>
      );
    });
  };

  const renderVariant = () => {
    switch (variant) {
      case TimedFeatureListVariant.CIRCULAR:
        return renderCircularVariant();
      case TimedFeatureListVariant.LINEAR:
        return renderLinearVariant();
      default:
        return renderCircularVariant();
    }
  };

  const renderGatsbyImage = () => {
    return (
      <AnimatePresence>
        {selectedImageData && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            key={selectedFeatureId}
          >
            <GatsbyImage
              image={selectedImageData}
              alt="Hero Banner"
              className="object-cover"
            />
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  const renderCarousel = () => {
    return (
      <div className="block lg:hidden mt-6 mb-3">
        <CarouselComponent
          showIndicators
          transitionTime={times}
          showArrows={false}
        >
          {renderTimedFeatureForMobileView() ?? [<></>]}
        </CarouselComponent>
      </div>
    );
  };

  const renderCircularVariant = () => {
    return (
      <div
        className="py-12 md:py-[60px] xl:py-20 px-8 xl:px-[140px] lg:flex justify-between relative gap-2"
        style={{ backgroundColor: bgColor ?? "#FFFFFF" }}
      >
        <div className="xl:w-1/2 grow-0 shrink-0 ">
          <Title
            titleText={title}
            variant="h2"
            className="max-w-[80%] md:max-w-[400px] xl:max-w-none"
          />
          <Title
            titleText={subTitle}
            variant="paragraph"
            className="sm:max-w-[60%] md:max-w-[400px] xl:max-w-[551px]"
          />
          <Link href={learnMoreLink} linkText={learnMoreText} />
          <div className="mt-12 hidden lg:block md:max-w-[340px] lg:max-w-[400px] xl:max-w-none">
            {renderTimedFeatureList()}
          </div>
        </div>
        <div className="hidden lg:flex items-center w-1/2 overflow-hidden grow-0">
          {renderGatsbyImage()}
        </div>
        {renderCarousel()}
      </div>
    );
  };

  const renderLinearVariant = () => {
    return (
      <VizWrapper
        style={{ backgroundColor: bgColor ?? "#FFFFFF" }}
        className="px-8 lg:px-[8%] flex flex-col lg:items-center"
      >
        <div>
          <Title
            titleText={title}
            variant="h2"
            className="xl:max-w-[70%] mr-0 text-left lg:text-center ml-0 lg:mx-auto"
          />
          <Title
            titleText={subTitle}
            variant="paragraph"
            className="md:max-w-[400px] xl:max-w-[551px]"
          />
          <Link href={learnMoreLink} linkText={learnMoreText} />
        </div>
        <div
          className={twMerge(
            "hidden lg:flex justify-center relative gap-20 items-end",
            imagePositionClass
          )}
        >
          <div className={twMerge("grow-0")}>
            <div
              className={twMerge(
                "mt-12 hidden lg:block md:max-w-[340px] lg:max-w-[400px] xl:max-w-none",
                isImageLeft ? "" : ""
              )}
            >
              {renderTimedFeatureList()}
            </div>
          </div>
          <div
            className={twMerge(
              "mt-12 hidden lg:block md:max-w-[340px] lg:max-w-[400px] xl:max-w-none w-1/2 shrink-0 min-w-[550px]"
            )}
          >
            {renderGatsbyImage()}
          </div>
        </div>
        <div className="w-full overflow-hidden">{renderCarousel()}</div>
      </VizWrapper>
    );
  };
  return renderVariant();
};
export default TimedFeatureList;
